import { useEffect } from 'react';
import { GRID_SIZE } from '../utils/whiteboardUtils';

export function useWhiteboardGlobalEffects({
  gridCanvasRef,
  resizing, setResizing,
  setShapes,
  selectedShape, setSelectedShape,
  setDragged, setEditingStickerId,
  shouldIgnoreKeyboardEvent,
  createTextBlock, lastMousePosition,
}) {
  // Lock body scroll while whiteboard is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Delete / Backspace — remove selected shape
  useEffect(() => {
    const handleKeyDown = e => {
      if (shouldIgnoreKeyboardEvent(e)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShape) {
        e.preventDefault();
        setShapes(prev => prev.filter(s => s.id !== selectedShape));
        setSelectedShape(null);
        setDragged(null);
        setEditingStickerId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedShape, shouldIgnoreKeyboardEvent, setShapes, setSelectedShape, setDragged, setEditingStickerId]);

  // Sticker / shape corner resize via mouse drag
  useEffect(() => {
    if (!resizing) return;

    function onMove(e) {
      const { shape, startX, startY, startW, startH, startX0, startY0, corner } = resizing;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      let newW = startW, newH = startH, newX = startX0, newY = startY0;
      if (corner[0] === 1) newW = Math.max(60, startW + dx);
      if (corner[0] === 0) { newW = Math.max(60, startW - dx); newX = startX0 + dx; }
      if (corner[1] === 1) newH = Math.max(60, startH + dy);
      if (corner[1] === 0) { newH = Math.max(60, startH - dy); newY = startY0 + dy; }
      setShapes(prev => prev.map(s =>
        s.id === shape.id ? { ...s, w: newW, h: newH, x: newX, y: newY } : s
      ));
    }

    function onUp() { setResizing(null); }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, setResizing, setShapes]);

  // Draw static dot grid on canvas
  useEffect(() => {
    const canvas = gridCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.fillStyle = '#e6e6ee';
    ctx.shadowBlur = 0;
    const dotRadius = 1.2;
    for (let x = 0; x < canvas.width; x += GRID_SIZE) {
      for (let y = 0; y < canvas.height; y += GRID_SIZE) {
        ctx.beginPath();
        ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
    ctx.restore();
  }, [gridCanvasRef]);

  // Paste plain text via Ctrl+V anywhere on page
  useEffect(() => {
    const handlePaste = async (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        const clipboardData = e.clipboardData || window.clipboardData;
        let text = clipboardData?.getData ? clipboardData.getData('text/plain') : '';
        if (!text) text = await navigator.clipboard.readText();
        if (text && text.trim()) {
          const x = lastMousePosition.current?.x || window.innerWidth / 2;
          const y = lastMousePosition.current?.y || window.innerHeight / 2;
          createTextBlock(x, y, text);
        }
      } catch (_) {}
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
